/**
 * @format
 * General user related API functions
 * Abstracting these out from DataMixin.js.
 */
import axios from 'axios';

/**
 * Check username to determine if it's valid and (optionally) free for use
 *
 * @param {String} username   Username
 * @param {mixed}  lookupType 0 (default) = Must be free for use, 1 = Simply must be a valid email
 *
 * @return Promise for resolve, reject
 */
export const userIsUsernameValid = function (username, lookupType = 0) {
    return axios
        .get(naRoutes.userUsernameValid + '/' + username + '/' + lookupType)
        .then((response) => Promise.resolve(response.data))
        .catch((error) => Promise.reject(error));
};

/**
 * Check screen name to determine if it's valid and free for use
 *
 * @param {String} screenname Screen name
 *
 * @return Promise for resolve, reject
 */
export const userIsScreennameValid = function (screenname) {
    return axios
        .get(naRoutes.userScreennameValid + '/' + screenname)
        .then((response) => response.data);
};

/**
 * Register
 *
 * @param {Object} data Registration data
 *
 * @return Promise for resolve, reject
 */
export function userRegister(data) {
    return axios
        .post(naRoutes.register, {
            data
        })
        .then((response) => response.data);
}

export function scorePassword(password) {
    return axios
        .post(naRoutes.scorePassword, {
            password
        })
        .then((response) => response.data);
}

export function registerQuick(data) {
    return axios
        .post('/register_quick', data)
        .then((response) => response.data);
}

/**
 * Login
 *
 * @param {String}  email    Email Addresss
 * @param {String}  password New password
 * @param {Boolean} remember Remember me?
 * @param {mixed}   postId   May be a post id.  If given, we'll return the post identifier
 *
 * @return Promise for resolve, reject
 */
export const userLogin = function (
    email,
    password,
    remember = false,
    postId = null
) {
    const data = {
        email,
        password,
        remember,
        postId
    };
    return axios
        .post(naRoutes.login, {
            data
        })
        .then((response) => response.data);
};

/**
 * Login
 *
 * @param {String}  email    Email Addresss
 * @param {Number}  One Time Password
 * @param {Boolean} remember Remember me?
 * @param {mixed}   postId   May be a post id.  If given, we'll return the post identifier
 *
 * @return Promise for resolve, reject
 */
export const userOtpLogin = function (
    email,
    code,
    remember = false,
) {
    const data = {
        email,
        code,
        remember,
    };
    return axios
        .post(naRoutes.otpLogin, {
            data
        })
        .then((response) => response.data);
};

/**
 * Promise-wrapped facebook login
 */
function fbLogin() {
    return new Promise((resolve, reject) => {
        window.FB.login(
            (response) => {
                if (response.authResponse) {
                    window.FB.api(
                        '/me',
                        { fields: 'id,first_name,last_name,email' },
                        function ({ email }) {
                            resolve({
                                accessToken: response.authResponse.accessToken,
                                email
                            });
                        }
                    );
                } else {
                    reject(new Error('Facebook login failed.'));
                }
            },
            { scope: 'public_profile,email', return_scopes: true }
        );
    });
}

/**
 * Login
 *
 * @return Promise for resolve, reject
 */
export async function userLoginSocial(socialType, params = {}) {
    const mergedParams = {
        remember: false,
        postId: null,
        ...params
    };
    const MAGIC_TOKEN = '1jj4j29929jJDHJQH2111aa;3';
    let accessToken = null;
    let email = '';
    let firstName = null;
    let lastName = null;
    if (socialType === 'google') {
        // Store the client ID we're using for logging purposes (in outer scope)
        let usedClientId = null;
        
        try {
            // Check if Google Identity Services is properly initialized
            if (!window.google || !window.google.accounts || !window.google.accounts.id) {
                throw new Error('Google Identity Services not loaded');
            }
            
            // Get the current client ID if already initialized
            usedClientId = window.google.accounts.id._clientId;
            
            // Check if Google Identity Services has been initialized with a client ID
            if (!usedClientId) {
                console.warn('Google Identity Services not initialized with client ID, attempting to initialize...');
                
                // Try to get client ID from meta tag, with retry logic
                let clientId = document.querySelector('meta[name="google-signin-client_id"]')?.content;
                
                // If not found, wait a bit for the DOM to be ready (for popup windows)
                if (!clientId) {
                    console.log('Client ID not found immediately, waiting for DOM...');
                    // Try to get it from parent window if we're in a popup
                    if (window.opener) {
                        try {
                            const parentClientId = window.opener.document.querySelector('meta[name="google-signin-client_id"]')?.content;
                            if (parentClientId) {
                                console.log('Got client ID from parent window:', parentClientId);
                                clientId = parentClientId;
                            }
                        } catch (e) {
                            console.warn('Could not access parent window:', e);
                        }
                    }
                    
                    // If still not found, wait a bit and try again
                    if (!clientId) {
                        await new Promise(resolve => setTimeout(resolve, 100));
                        clientId = document.querySelector('meta[name="google-signin-client_id"]')?.content;
                    }
                }
                
                if (clientId) {
                    try {
                        window.google.accounts.id.initialize({
                            client_id: clientId,
                            callback: window.handleCredentialResponse
                        });
                        console.log('Google Identity Services initialized with client ID:', clientId);
                        usedClientId = clientId; // Store for logging
                    } catch (error) {
                        console.error('Error initializing Google Identity Services:', error);
                        if (error.message && error.message.includes('origin') && error.message.includes('not allowed')) {
                            const origin = window.location.origin;
                            throw new Error(`Google Sign-In configuration error: The origin "${origin}" is not authorized for client ID "${clientId}". Please add "${origin}" to the Authorized JavaScript origins in Google Cloud Console.`);
                        }
                        throw error;
                    }
                } else {
                    console.error('Google client ID not found in meta tag after waiting');
                    console.log('Available meta tags:', Array.from(document.querySelectorAll('meta')).map(m => ({ name: m.name, content: m.content })));
                    throw new Error('Google client ID not found in meta tag. Please ensure the page has loaded completely.');
                }
            } else {
                // If already initialized, try to get it from meta tag for logging
                if (!usedClientId) {
                    usedClientId = document.querySelector('meta[name="google-signin-client_id"]')?.content;
                }
            }
            
            // Use Google Identity Services with proper popup flow
            console.log('Starting Google sign-in popup flow...');
            console.log('Google Identity Services available:', {
                google: !!window.google,
                accounts: !!(window.google && window.google.accounts),
                id: !!(window.google && window.google.accounts && window.google.accounts.id),
                renderButton: !!(window.google && window.google.accounts && window.google.accounts.id && window.google.accounts.id.renderButton)
            });
            
            // Wait for the credential response
            const response = await new Promise((resolve, reject) => {
                const timeout = setTimeout(() => {
                    console.error('Google sign-in timeout - no response received');
                    reject(new Error('Google sign-in timeout'));
                }, 30000); // 30 second timeout
                
                // Store the resolve/reject functions globally so they can be called from the callback
                window.googleSignInResolve = resolve;
                window.googleSignInReject = reject;
                window.googleSignInTimeout = timeout;
                
                // Create a temporary button and trigger the popup
                const tempDiv = document.createElement('div');
                tempDiv.style.position = 'absolute';
                tempDiv.style.left = '-9999px';
                tempDiv.style.top = '-9999px';
                document.body.appendChild(tempDiv);
                
                // Render the Google sign-in button with a small delay to ensure everything is ready
                console.log('Rendering Google sign-in button...');
                // Get the actual client ID being used (from stored value or meta tag)
                const actualClientId = usedClientId || window.google.accounts.id._clientId || document.querySelector('meta[name="google-signin-client_id"]')?.content;
                console.log('Client ID being used:', actualClientId || 'NOT FOUND');
                console.log('Current origin:', window.location.origin);
                if (!actualClientId) {
                    console.error('WARNING: No client ID available for Google sign-in!');
                }
                
                setTimeout(() => {
                    try {
                        // Note: window.google.accounts.id._clientId might be undefined even after initialization
                        // This is normal - Google doesn't always expose this property
                        // The client ID is passed during initialization and renderButton uses it internally
                        const clientIdFromMeta = document.querySelector('meta[name="google-signin-client_id"]')?.content;
                        console.log('DEBUG: About to render button - Client ID check:', {
                            _clientId: window.google.accounts.id._clientId,
                            metaTag: clientIdFromMeta,
                            usedClientId: usedClientId,
                            note: '_clientId may be undefined even after successful initialization (this is normal)'
                        });
                        
                        // Don't re-initialize here - it's already initialized earlier
                        // Re-initializing can cause the "origin not allowed" error even when everything is correct
                        // Google Identity Services remembers the client ID from the initial initialize() call
                        
                        window.google.accounts.id.renderButton(tempDiv, {
                            theme: 'outline',
                            size: 'large',
                            type: 'standard',
                            shape: 'rectangular',
                            text: 'signin_with',
                            width: 200
                        });
                        console.log('Google sign-in button render call completed');
                    } catch (error) {
                        console.error('Error rendering Google sign-in button:', error);
                        console.error('Error details:', {
                            message: error.message,
                            stack: error.stack,
                            clientId: window.google.accounts.id._clientId,
                            origin: window.location.origin
                        });
                        
                        // Clean up
                        if (document.body.contains(tempDiv)) {
                            document.body.removeChild(tempDiv);
                        }
                        clearTimeout(timeout);
                        
                        // Check if it's an origin error
                        if (error.message && error.message.includes('origin') && error.message.includes('not allowed')) {
                            const origin = window.location.origin;
                            const clientId = window.google.accounts.id._clientId;
                            reject(new Error(`Google Sign-In configuration error: The origin "${origin}" is not authorized for client ID "${clientId}". Please add "${origin}" to the Authorized JavaScript origins in Google Cloud Console.`));
                        } else {
                            reject(new Error('Failed to render Google sign-in button: ' + error.message));
                        }
                        return;
                    }
                }, 100); // Small delay to ensure everything is ready
                
                // Wait for the button to render and then click it
                const waitForButton = () => {
                    const button = tempDiv.querySelector('div[role="button"]');
                    const allButtons = tempDiv.querySelectorAll('div');
                    const elapsed = Date.now() - startTime;
                    
                    // Check if there's an error message in the div (Google sometimes shows errors in the div)
                    const errorElements = tempDiv.querySelectorAll('[class*="error"], [id*="error"]');
                    if (errorElements.length > 0) {
                        const errorText = Array.from(errorElements).map(el => el.textContent).join(' ');
                        console.error('Google sign-in error detected in rendered content:', errorText);
                        if (document.body.contains(tempDiv)) {
                            document.body.removeChild(tempDiv);
                        }
                        clearTimeout(timeout);
                        const errorMessage = errorText || `Unknown error. Please check that "${window.location.origin}" is added to Authorized JavaScript origins in Google Cloud Console for client ID "${window.google.accounts.id._clientId}".`;
                        reject(new Error(`Google Sign-In error: ${errorMessage}`));
                        return;
                    }
                    
                    console.log(`Checking for Google sign-in button (${elapsed}ms elapsed)...`, {
                        buttonFound: !!button,
                        totalDivs: allButtons.length,
                        tempDivHTML: tempDiv.innerHTML.substring(0, 200) + '...'
                    });
                    
                    if (button) {
                        console.log('Google sign-in button found, clicking...');
                        button.click();
                    } else {
                        // Check if we've been waiting too long
                        if (elapsed > 10000) { // 10 second timeout
                            console.error('Google sign-in button not found after 10 seconds');
                            console.log('Final tempDiv content:', tempDiv.innerHTML);
                            console.log('Client ID:', window.google.accounts.id._clientId);
                            console.log('Origin:', window.location.origin);
                            if (document.body.contains(tempDiv)) {
                                document.body.removeChild(tempDiv);
                            }
                            clearTimeout(timeout);
                            const configMessage = `This may be due to origin configuration. Please verify that "${window.location.origin}" is added to Authorized JavaScript origins in Google Cloud Console for client ID "${window.google.accounts.id._clientId}".`;
                            reject(new Error(`Google sign-in button not found. ${configMessage}`));
                        } else {
                            // Try again in 100ms
                            setTimeout(waitForButton, 100);
                        }
                    }
                };
                
                const startTime = Date.now();
                // Start checking for the button after the render delay
                setTimeout(waitForButton, 200);
            });

            console.log('Google sign-in response received:', response);
            
            // Decode the JWT token to get user info
            const payload = JSON.parse(atob(response.credential.split('.')[1]));
            email = payload.email;
            firstName = payload.given_name || '';
            lastName = payload.family_name || '';
            accessToken = response.credential;
        } catch (error) {
            if (error?.error === 'popup_closed_by_user' || error?.message === 'cancelled') {
                throw new Error('cancelled');
            }
            throw error;
        }
    } else if (socialType === 'facebook') {
        const result = await fbLogin();
        email = result.email;
        accessToken = result.accessToken;
    } else if (socialType === 'apple') {
        try {
            const result = await window.AppleID.auth.signIn();
            if (result.user) {
                email = result.user.email;
                if (result.user.name && result.user.name.firstName) {
                    firstName = result.user.name.firstName;
                }
                if (result.user.name && result.user.name.lastName) {
                    lastName = result.user.name.lastName;
                }
            }
            accessToken = result.authorization.id_token;
        } catch (error) {
            if (error?.error === 'popup_closed_by_user') {
                throw new Error('cancelled');
            }
            throw error;
        }
    }

    const data = {
        ...mergedParams,
        accessToken,
        socialType,
        firstName,
        lastName,
        magicToken: MAGIC_TOKEN,
        fromGaa: params.fromGaa || false // Indicate if this is from Google Extended Access
    };
    async function retryCallback({ email, password, acceptTc = false }) {
        try {
            const response = await axios.post(naRoutes.loginSocial, {
                data: {
                    ...data,
                    email,
                    password,
                    acceptTc,
                    fromGaa: data.fromGaa || false // Preserve fromGaa flag in retry
                }
            });
            if (
                response.data &&
                (response.data.email_required ||
                    response.data.password_required ||
                    response.data.accept_tc_required)
            ) {
                return {
                    retryCallback,
                    email,
                    socialType,
                    emailRequired: response.data.email_required,
                    passwordRequired: response.data.password_required,
                    acceptTcRequired: response.data.accept_tc_required
                };
            }
            return response.data;
        } catch (error) {
            if (
                error.response &&
                error.response.data &&
                error.response.data.errors
            ) {
                return {
                    success: false,
                    errors: [error.response.data.errors]
                };
            }
            return {
                success: false,
                errors: [error.message]
            };
        }
    }
    try {
        console.log('Sending social login request to:', naRoutes.loginSocial);
        console.log('Request data:', { data });
        console.log('CSRF token:', window.Laravel?.csrfToken);
        console.log('Axios headers:', axios.defaults.headers.common);
        
        // CSRF verification is disabled for /login/social route
        
        const response = await axios.post(naRoutes.loginSocial, { data });
        console.log('Social login response:', response);
        
        if (
            response.data &&
            (response.data.email_required ||
                response.data.password_required ||
                response.data.accept_tc_required)
        ) {
            return {
                retryCallback,
                email,
                socialType,
                emailRequired: response.data.email_required,
                passwordRequired: response.data.password_required,
                acceptTcRequired: response.data.accept_tc_required
            };
        }
        return response.data;
    } catch (error) {
        console.error('Social login error:', error);
        console.error('Error response:', error.response);
        console.error('Error response data:', error.response?.data);
        console.error('Error response status:', error.response?.status);
        console.error('Error response headers:', error.response?.headers);
        
        if (
            error.response &&
            error.response.data &&
            error.response.data.errors
        ) {
            return {
                success: false,
                errors: [error.response.data.errors]
            };
        }
        return {
            success: false,
            errors: [error.message]
        };
    }
}

/**
 * Send forgot password last
 *
 * @param {String} email Email Addresss
 *
 * @return Promise for resolve, reject
 */
export function userForgotPassword(email) {
    const data = {
        email
    };
    return axios
        .post(naRoutes.forgotPassword, {
            data
        })
        .then((response) => response.data);
}

/**
 * Reset password
 *
 * @param {String} email           Email Addresss
 * @param {String} token           Reset token
 * @param {String} password        New password
 * @param {String} passwordConfirm Password confirmation
 *
 * @return Promise for resolve, reject
 */
export function userResetPassword(email, token, password, passwordConfirm) {
    const data = {
        email,
        token,
        password,
        password_confirmation: passwordConfirm
    };
    return axios
        .post(naRoutes.resetPassword, {
            data
        })
        .then((response) => response.data);
}

/**
 * Confirm registration
 *
 * @param {String} email Email Addresss
 * @param {String} token Confirmation ode
 *
 * @return Promise for resolve, reject
 */
export function userConfirmRegistration(email, token) {
    const data = {
        email,
        token
    };
    return axios
        .post(naRoutes.registerConfirm, {
            data
        })
        .then((response) => response.data);
}

/**
 * Request new confirm registration code
 *
 * @param {String} email Email Addresss
 *
 * @return Promise for resolve, reject
 */
export const userConfirmRegistrationRequest = function (email) {
    const data = {
        email
    };
    return axios
        .post(naRoutes.registerConfirmRequest, {
            data
        })
        .then((response) => response.data);
};

/**
 * Load current user profile data
 *
 * @return Promise for resolve, reject
 */
export const userDataLoadProfile = function () {
    return axios.get(naRoutes.loadProfile).then((response) => response.data);
};

/**
 * Load current user profile data
 *
 * @return Promise for resolve, reject
 */
export const userAccountDelete = function (id) {
    return axios.post(naRoutes.deleteAccount ,{
        id
    }).then((response) => response.data);
};

/**
 * Save user profile data
 *
 * @param {Object} data Data to pass
 *
 * @return Promise for resolve, reject
 */
export const userDataSaveProfile = function (data) {
    return axios
        .post(naRoutes.saveProfile, {
            data
        })
        .then((response) => response.data);
};

/**
 * Save user publications preferences
 *
 * @param {Object} data Data to pass
 *
 * @return Promise for resolve, reject
 */
export const userDataSavePublicationPreferences = function (data) {
    return axios
        .post(naRoutes.savePublicationPreferences, {
            data
        })
        .then((response) => response.data);
};

/**
 * Load current user data object
 *
 * @return Promise for resolve, reject
 */
export function userDataLoadInfo() {
    return axios.get(naRoutes.loadUserData).then((response) => response.data);
}

/**
 * Signup interested (for mailing list)
 *
 * @param {Object} data Signup data
 *
 * @return Promise for resolve, reject
 */
export const userInterested = function (data) {
    return axios
        .post(naRoutes.interested, {
            data
        })
        .then((response) => response.data);
};

/**
 * Save user personal data (e.g. address)
 *
 * @param {Object} data Data to pass
 *
 * @return Promise for resolve, reject
 */
export const userSaveWho = function (data) {
    return axios
        .post(naRoutes.saveWho, {
            data
        })
        .then((response) => response.data);
};

/**
 * Load user personal data (e.g. address)
 *
 * @return Promise for resolve, reject
 */
export const userLoadWho = function () {
    return axios.get(naRoutes.loadWho).then((response) => response.data);
};

/**
 * Load users
 *
 * @param {Object} data Data to pass
 *
 * @return Promise for resolve, reject
 */
export const readUsers = function (data) {
    return axios
        .post(naRoutes.readUsers, {
            data
        })
        .then((response) => response.data);
};

/**
 * Load user
 *
 * @param {Object} data Data to pass
 *
 * @return Promise for resolve, reject
 */
export const readUser = function (data) {
    return axios
        .post(naRoutes.readUser, {
            data
        })
        .then((response) => response.data);
};

/**
 * Send OTP to user
 *
 * @param {Object} data Data
 *
 * @return Promise for resolve, reject
 */
export const sendUserOtp = function (data) {
    return axios
        .post(naRoutes.sendUserOtp, {
            data
        })
        .then((response) => response.data);
};
